// Shared types + helpers for the Collections page (Task #4830).

import type { ResolvedCollectionSettings, CollectionRuleField } from "@shared/collections-settings";

/** Row shape returned by GET /api/app/collections/properties. */
export type CollectionPropertyRow = {
  propertyId: number;
  name: string;
  address: string | null;
  status: string | null;
  propertyGroup: string | null;
  enabled: boolean;
  lastRunAt: string | null;
  overrides: {
    negativeBalanceThreshold: string | null;
    paymentPercentage: string | null;
    paymentFrequency: string | null;
    paymentDeadlineDays: number | null;
    offerLimit: number | null;
    offerResetDays: number | null;
    cooldownDays: number | null;
  } | null;
  resolved: ResolvedCollectionSettings;
};

export type CollectionPropertiesResponse = {
  accountEnabled: boolean;
  properties: CollectionPropertyRow[];
};

/** One member-level decision from a run or preview. */
export type CollectionsRunDecision = {
  propertyId: number;
  propertyName: string | null;
  memberId: number;
  memberName: string;
  amountDue: number;
  outcome: "sent" | "skipped" | "failed";
  skipReason: string | null;
  messagePreview: string | null;
};

export type CollectionsRunReport = {
  runId: string;
  status: "ok" | "account_disabled" | "no_enabled_properties";
  processed: number;
  noticed: number;
  skipped: number;
  failed: number;
  propertiesProcessed: number;
  errors: string[];
  decisions: CollectionsRunDecision[];
};

export type CollectionActivityRow = {
  id: number;
  runId: string;
  runType: string;
  propertyId: number | null;
  propertyName: string | null;
  resolvedPropertyName: string | null;
  memberId: number | null;
  memberName: string;
  amountDue: string | null;
  outcome: string;
  skipReason: string | null;
  messagesPendingId: number | null;
  messageContent: string | null;
  reconciledChatId: number | null;
  createdAt: string;
};

export type CollectionActivityResponse = {
  rows: CollectionActivityRow[];
  total: number;
  page: number;
  limit: number;
};

/** Human labels for the engine's skip vocabulary. */
export const SKIP_REASON_LABELS: Record<string, string> = {
  below_threshold: "Below threshold",
  offer_limit_reached: "Offer limit reached",
  cooldown_active: "In cooldown",
  no_administrator: "No host account",
  no_host: "Member has no host",
  duplicate_claim: "Already contacted today",
  enqueue_failed: "Send failed",
  publish_failed: "Delivery failed",
};

export const RULE_FIELD_LABELS: Record<CollectionRuleField, string> = {
  negativeBalanceThreshold: "Balance threshold",
  paymentPercentage: "Payment percentage",
  paymentFrequency: "Payment frequency",
  paymentDeadlineDays: "Payment deadline (days)",
  offerLimit: "Offer limit",
  offerResetDays: "Offer reset window (days)",
  cooldownDays: "Cooldown (days)",
};

export function formatMoney(value: number | string | null | undefined): string {
  const n = typeof value === "number" ? value : parseFloat(String(value ?? ""));
  if (!Number.isFinite(n)) return "—";
  return `$${Math.abs(n).toFixed(2)}`;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}
